import {useMemo} from 'react';

import InfoIcon from '@mui/icons-material/Info';
import {Typography} from '@mui/material';
import {Box} from '@mui/system';
import {useTranslation} from 'react-i18next';

import {RolesAccount} from '@/app/auth/constants/AuthConstants';
import {Note, NoteType} from '@/app/shared-components';
import {COLORS} from '@/styles/colors';

import {AuthenticationWrapper} from '../../components/AuthenticationWrapper';

import {
  SignUpStep1FormFragment,
  SignUpStep2FormFragment,
  SignUpStep3FormFragment,
} from './fragments';
import {SIGN_UP_STEP} from './models/constants';
import {
  SignUpViewModelProvider,
  useSignUpViewModelProvider,
} from './ViewModelContext';

import './styles';

const SignUpContent = () => {
  const {t} = useTranslation();

  const {
    currentStep,
    phone,
    password,
    confirmPassword,
    firstName,
    lastName,
    email,
    signUpCode,
    verifyList,
    showInputReferralCode,
    isDisabledSignUpStep1,
    isDisabledSignUpStep2,
    isDisabledSignUpStep3,
    isValidSignUpCode,
    setStep,
    setSignUpCode,
    setPhone,
    setFirstName,
    setLastName,
    handleOnClickSignUpStep1,
    handleOnClickSignUpStep2,
    handleSignUp,
    handleOnClickSignIn,
    setPassword,
    setConfirmPassword,
    setEmail,
    setEmailError,
    setIsVerify,
    setShowInputReferralCode,
    isCheckboxChecked,
    setIsCheckboxChecked,
    isMarketingChecked,
    handleToggleMarketingChecked,
    role,
    isCountryCodeVisible,
    isPhonePrefilledFromInvitation,
    coParentInviterName,
    showCompatibilityBanner,
    isSignUpDisabled,
    isMobile,
    handleOpenTermsAndConditions,
    handleOpenPrivacyPolicy,
    isLoadingAmplifyAuth,
  } = useSignUpViewModelProvider();

  const compatibilityBanner = useMemo(() => {
    if (!showCompatibilityBanner) {
      return null;
    }

    return (
      <Box className="compatibility-banner">
        <Box className="compatibility-icon-container">
          <InfoIcon style={{color: COLORS.ON_SURFACE_VARIANT}} />
        </Box>
        <Box className="compatibility-text-container">
          <Typography className="compatibility-text-data">
            {t('sign_up.operating_system_alert')}
          </Typography>
        </Box>
      </Box>
    );
  }, [showCompatibilityBanner, t]);

  const authenticationWrapperTitle = useMemo(() => {
    if (
      currentStep === SIGN_UP_STEP.PERSONAL_INFO &&
      role === RolesAccount.PARENT
    ) {
      return (
        <Box className="signup-title-container">
          <Typography
            className="signup-title"
            component="div">
            {t('sign_up.title_parent')}
          </Typography>
          <Typography
            className="signup-title signup-title-secondary"
            component="div">
            {t('sign_up.title_parent_2')}
          </Typography>
        </Box>
      );
    }

    if (currentStep === SIGN_UP_STEP.PERSONAL_INFO) {
      return (
        <Typography
          className="signup-title"
          component="div">
          {t('sign_up.title_teen')}
        </Typography>
      );
    }

    return (
      <Typography
        className="signup-title"
        component="div">
        {currentStep === SIGN_UP_STEP.CONTACT_INFO
          ? t('sign_up.title_2')
          : t('sign_up.title_3')}
      </Typography>
    );
  }, [currentStep, role, t]);

  return (
    <AuthenticationWrapper
      title={authenticationWrapperTitle}
      role={role}
      showBrands={!isMobile || currentStep === SIGN_UP_STEP.PERSONAL_INFO}
      showFooter={isMobile && currentStep === SIGN_UP_STEP.PERSONAL_INFO}
      onBack={
        currentStep === SIGN_UP_STEP.PERSONAL_INFO
          ? null
          : () => setStep(currentStep - 1)
      }>
      {isSignUpDisabled && (
        <Box
          className="signup-disabled-banner"
          sx={{paddingInline: '16px', paddingBottom: '16px'}}>
          <Note
            type={NoteType.ALERT}
            text={t('sign_up.sign_up_disabled_note')}
          />
        </Box>
      )}
      {currentStep === SIGN_UP_STEP.PERSONAL_INFO && (
        <SignUpStep1FormFragment
          firstName={firstName}
          lastName={lastName}
          signUpCode={signUpCode}
          showInputReferralCode={showInputReferralCode}
          isMobile={isMobile}
          isValidSignUpCode={isValidSignUpCode}
          isDisabledSignUpStep1={isDisabledSignUpStep1}
          isLoadingAmplifyAuth={isLoadingAmplifyAuth}
          compatibilityBanner={compatibilityBanner}
          setFirstName={setFirstName}
          setLastName={setLastName}
          setSignUpCode={setSignUpCode}
          setShowInputReferralCode={setShowInputReferralCode}
          handleOnClickSignUpStep1={handleOnClickSignUpStep1}
          handleOnClickSignIn={handleOnClickSignIn}
        />
      )}
      {currentStep === SIGN_UP_STEP.CONTACT_INFO && (
        <SignUpStep2FormFragment
          phone={phone}
          email={email}
          isCheckboxChecked={isCheckboxChecked}
          isMarketingChecked={isMarketingChecked}
          isDisabledSignUpStep2={isDisabledSignUpStep2}
          isLoadingAmplifyAuth={isLoadingAmplifyAuth}
          isCountryCodeVisible={isCountryCodeVisible}
          isMobile={isMobile}
          isPhonePrefilledFromInvitation={isPhonePrefilledFromInvitation}
          coParentInviterName={coParentInviterName}
          setPhone={setPhone}
          setEmail={setEmail}
          setEmailError={setEmailError}
          setIsCheckboxChecked={setIsCheckboxChecked}
          handleToggleMarketingChecked={handleToggleMarketingChecked}
          handleOpenTermsAndConditions={handleOpenTermsAndConditions}
          handleOpenPrivacyPolicy={handleOpenPrivacyPolicy}
          handleOnClickSignUpStep2={handleOnClickSignUpStep2}
        />
      )}
      {currentStep === SIGN_UP_STEP.PASSWORD && (
        <SignUpStep3FormFragment
          password={password}
          confirmPassword={confirmPassword}
          verifyList={verifyList}
          isDisabledSignUpStep3={isDisabledSignUpStep3}
          isLoadingAmplifyAuth={isLoadingAmplifyAuth}
          isMobile={isMobile}
          setPassword={setPassword}
          setConfirmPassword={setConfirmPassword}
          setIsVerify={setIsVerify}
          handleSignUp={handleSignUp}
        />
      )}
    </AuthenticationWrapper>
  );
};

const SignUpPage = () => (
  <SignUpViewModelProvider>
    <SignUpContent />
  </SignUpViewModelProvider>
);

export default SignUpPage;
